import React from 'react';
import { Plus, Smile, Camera, Mic } from 'lucide-react';

const LOGO = '/kolanut_logo.png';

/** Link styling only (not clickable; mock UI) */
export function WaLink({ children }) {
  return (
    <span className="text-[#53bdeb] underline underline-offset-2 decoration-[#53bdeb]/70">
      {children}
    </span>
  );
}

/**
 * Shared WhatsApp-style phone body (status bar, header, messages, input).
 * Used by desktop and mobile hero visuals.
 */
export default function WhatsAppHeroPhone({ className = '', mobile = false }) {
  const messages = [
    {
      from: 'owner',
      time: '5:32 PM',
      body: (
        <>
          What&apos;s driving revenue right now: <strong className="font-semibold">Shopify</strong> orders or{' '}
          <strong className="font-semibold">Meta</strong> ads?
        </>
      ),
    },
    {
      from: 'kolanut',
      time: '5:33 PM',
      body: (
        <>
          Shopify <strong className="font-semibold text-[#e9edef]">$18.4k</strong> (
          <WaLink>order breakdown</WaLink>). Meta <strong className="font-semibold text-[#e9edef]">$9.2k</strong> (
          <WaLink>campaign report</WaLink>). Shift <strong className="font-semibold text-[#e9edef]">$500</strong> from cold
          audiences. Approve?
        </>
      ),
    },
    {
      from: 'owner',
      time: '5:33 PM',
      body: <>Approved. Move it.</>,
    },
    {
      from: 'kolanut',
      time: '5:34 PM',
      body: (
        <>
          Done. <WaLink>Google Ads</WaLink> + <WaLink>Meta Ads</WaLink> updated. I&apos;ll ping if ROAS drops below{' '}
          <strong className="font-semibold text-[#e9edef]">2.5×</strong> (<WaLink>live dashboard</WaLink>).
        </>
      ),
    },
    {
      from: 'owner',
      time: '5:35 PM',
      body: <>Anything off on Square this week?</>,
    },
    {
      from: 'kolanut',
      time: '5:35 PM',
      body: (
        <>
          All clear. Payouts and refunds look normal (<WaLink>Square snapshot</WaLink>). I&apos;ll include it in
          tonight&apos;s <strong className="font-semibold text-[#e9edef]">8pm</strong> digest.
        </>
      ),
    },
  ];

  return (
    <div
      className={`rounded-[3rem] border-[10px] border-gray-900 bg-gray-900 shadow-[0_32px_80px_-14px_rgba(0,0,0,0.38)] overflow-hidden ring-1 ring-white/10 flex flex-col w-full min-h-0 ${className}`}
    >
      <div className="flex flex-col flex-1 min-h-0 bg-[#0b141a]">
        <div
          className={`flex items-center justify-between bg-[#202c33] shrink-0 ${mobile ? 'px-3 py-1' : 'px-4 py-1.5'}`}
        >
          <span
            className={`font-semibold text-[#e9edef] tabular-nums ${mobile ? 'text-[10px]' : 'text-[11px]'}`}
          >
            9:41
          </span>
          <div className={`flex items-center gap-1 text-[#e9edef] ${mobile ? 'text-[9px]' : ''}`}>
            <span className={`opacity-90 ${mobile ? 'text-[9px]' : 'text-[10px]'}`}>●●●</span>
            <span className={`font-semibold ${mobile ? 'text-[9px]' : 'text-[10px]'}`}>100%</span>
          </div>
        </div>

        <header
          className={`flex items-center bg-[#202c33] shrink-0 border-b border-black/25 ${mobile ? 'gap-2 px-2 py-2' : 'gap-2.5 px-2.5 py-2.5'}`}
        >
          <button type="button" className="text-[#aebac1] p-1 -ml-0.5 shrink-0" aria-hidden tabIndex={-1}>
            <svg
              className={mobile ? 'w-4 h-4' : 'w-[18px] h-[18px]'}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div
            className={`relative rounded-full bg-white flex items-center justify-center overflow-hidden ring-2 ring-[#25d366]/35 shrink-0 ${mobile ? 'h-8 w-8' : 'h-9 w-9'}`}
          >
            <img src={LOGO} alt="Kolanut" className={mobile ? 'h-6 w-6 object-contain' : 'h-7 w-7 object-contain'} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`font-semibold text-[#e9edef] leading-tight truncate ${mobile ? 'text-[12px]' : 'text-[13px]'}`}
            >
              Kolanut
            </p>
            <p className={`text-[#8696a0] leading-tight ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
              Business Assistant, online
            </p>
          </div>
          <button type="button" className="text-[#aebac1] p-1 shrink-0" aria-hidden tabIndex={-1}>
            <svg className={mobile ? 'w-4 h-4' : 'w-[18px] h-[18px]'} fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
          </button>
        </header>

        <div className="flex flex-1 min-h-0 flex-col overflow-hidden bg-[#0b141a] bg-[radial-gradient(ellipse_at_50%_0%,rgba(30,42,49,0.45)_0%,transparent_50%)]">
          <p
            className={`shrink-0 text-center text-[#8696a0] ${mobile ? 'text-[9px] pt-1.5 pb-0.5' : 'text-[10px] pt-2 pb-1'}`}
          >
            Today
          </p>
          <div
            className={`flex min-h-0 flex-1 flex-col justify-start overflow-hidden pb-2 ${mobile ? 'gap-1 px-2' : 'gap-1.5 px-2.5'}`}
          >
            {messages.map((m, i) => {
              const isOwner = m.from === 'owner';
              return (
                <div key={i} className={`flex w-full ${isOwner ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[94%] rounded-md px-2 py-1.5 shadow-sm ${
                        isOwner
                          ? 'rounded-tr-sm bg-[#005c4b] text-[#e9edef]'
                          : 'rounded-tl-sm bg-[#202c33] text-[#e9edef]'
                      }`}
                    >
                      {!isOwner && (
                        <p
                          className={`font-semibold text-[#25d366] mb-0.5 ${mobile ? 'text-[8px]' : 'text-[9px]'}`}
                        >
                          Kolanut
                        </p>
                      )}
                      {isOwner && (
                        <p
                          className={`font-medium text-[#86cfb9] mb-0.5 text-right ${mobile ? 'text-[8px]' : 'text-[9px]'}`}
                        >
                          You
                        </p>
                      )}
                      <div
                        className={
                          mobile
                            ? 'text-[11px] leading-[1.45]'
                            : 'text-[12px] sm:text-[13px] leading-[1.42]'
                        }
                      >
                        {m.body}
                      </div>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <span className="text-[9px] text-[#8696a0] tabular-nums">{m.time}</span>
                      {isOwner && (
                        <span className="text-[#53bdeb] text-[10px] leading-none" aria-hidden>
                          ✓✓
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className={`flex items-center bg-[#202c33] border-t border-black/20 shrink-0 ${mobile ? 'gap-1 px-1.5 py-1.5 pb-2' : 'gap-1.5 px-1.5 py-2 pb-2.5'}`}
        >
          <button type="button" className="p-1.5 text-[#8696a0] shrink-0 rounded-full hover:bg-white/5" aria-hidden tabIndex={-1}>
            <Plus className={mobile ? 'w-4 h-4' : 'w-[18px] h-[18px]'} strokeWidth={2} />
          </button>
          <div
            className={`flex-1 rounded-full bg-[#2a3942] border border-white/5 flex items-center ${mobile ? 'min-h-[30px] px-2.5' : 'min-h-[34px] px-3'}`}
          >
            <span className={`text-[#8696a0] ${mobile ? 'text-[11px]' : 'text-[12px]'}`}>Message</span>
          </div>
          <button type="button" className="p-1.5 text-[#8696a0] shrink-0" aria-hidden tabIndex={-1}>
            <Smile className={mobile ? 'w-4 h-4' : 'w-[17px] h-[17px]'} strokeWidth={2} />
          </button>
          <button type="button" className="p-1.5 text-[#8696a0] shrink-0" aria-hidden tabIndex={-1}>
            <Camera className={mobile ? 'w-4 h-4' : 'w-[17px] h-[17px]'} strokeWidth={2} />
          </button>
          <button type="button" className="p-1.5 text-[#8696a0] shrink-0" aria-hidden tabIndex={-1}>
            <Mic className={mobile ? 'w-4 h-4' : 'w-[17px] h-[17px]'} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
